import { useEffect, useState } from "react";
import ComboBox from "@/components/ComboBox";
import { performWithUndo } from "@/lib/undo";
import api, { formatApiError } from "@/lib/api";
import { useAuth, canWrite } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FieldLabel } from "@/components/FieldLabel";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Trash2, Plus, Pencil, Undo2 } from "lucide-react";
import SearchInput from "@/components/SearchInput";
import TrainerPeek from "@/components/TrainerPeek";
import { useSearch } from "@/lib/search";
import { toast } from "sonner";
import { FieldError, invalidClass } from "@/components/FieldError";
import { useFieldErrors } from "@/lib/formErrors";

// One description per master drives the add row, the edit dialog, the table and the
// required-field check. Three hand-written copies of the same form was how the add row
// and the columns drifted apart — a trainer's phone was storable by the API and
// offered by no screen.
const MASTERS = {
  units: {
    noun: "unit",
    title: "Units",
    columns: [{ key: "name", label: "Name" }, { key: "location", label: "Location" }],
    fields: [
      { key: "name", label: "Unit name", required: true },
      { key: "location", label: "Location" },
    ],
  },
  departments: {
    noun: "department",
    title: "Departments",
    columns: [
      { key: "name", label: "Name" },
      { key: "unit_id", label: "Unit", render: (r, ctx) => ctx.units.find((x) => x.id === r.unit_id)?.name || "—" },
    ],
    fields: [
      { key: "name", label: "Department name", required: true },
      { key: "unit_id", label: "Unit", required: true, type: "select",
        options: (ctx) => ctx.units.map((u) => ({ value: u.id, label: u.name })) },
    ],
  },
  trainers: {
    noun: "trainer",
    title: "Trainers",
    columns: [
      { key: "name", label: "Name" },
      { key: "type", label: "Type" },
      { key: "organization", label: "Org" },
      { key: "expertise_area", label: "Expertise" },
    ],
    fields: [
      { key: "name", label: "Trainer name", required: true },
      { key: "type", label: "Type", required: true, initial: "external", type: "select",
        options: () => [{ value: "internal", label: "Internal" }, { value: "external", label: "External" }] },
      // An internal trainer is a colleague, and the column to say which colleague has
      // existed since the first migration with no field anywhere to set it — so the link
      // could only ever be made by the seed. It is what ties "who conducted this" to a
      // person on the employee register rather than to a spelling of their name.
      { key: "employee_id", label: "Which employee", type: "select",
        when: (form) => form.type === "internal",
        options: (ctx) => ctx.employees.map((e) => ({
          value: e.id, label: `${e.full_name} — ${e.employee_code}` })) },
      // Typed text that repeats: offered back, never enforced. A new agency has to be
      // enterable, so these are comboboxes and not dropdowns.
      { key: "organization", label: "Organization", type: "combo",
        options: (ctx) => ctx.hints.organizations || [] },
      { key: "expertise_area", label: "Expertise", type: "combo",
        options: (ctx) => ctx.hints.expertise_areas || [] },
      { key: "contact_email", label: "Email" },
      { key: "contact_phone", label: "Phone" },
    ],
  },
};

const KINDS = Object.keys(MASTERS);

// Stable references, one per master. Searching a record by its own text columns.
const SEARCH_FIELDS = {
  units: ["name", "location"],
  departments: ["name"],
  trainers: ["name", "type", "organization", "expertise_area", "contact_email", "contact_phone"],
};
const blankOf = (spec) => Object.fromEntries(spec.fields.map((f) => [f.key, f.initial ?? ""]));
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

// Mirrors the server's CRUD_REQUIRED so an obviously incomplete form does not need a
// round trip. The server stays the authority — this only answers sooner.
// A field can depend on another — "which employee" only means anything once the trainer
// is marked internal. Hidden fields are not asked for and so cannot be missing.
const visibleFields = (fields, form) => fields.filter((f) => !f.when || f.when(form || {}));

// A field that has just stopped being shown must not keep a value: marking a trainer
// external again would otherwise leave them still pointing at an employee, invisibly and
// with no way to clear it.
const applyFieldChange = (fields, form, key, value) => {
  const next = { ...form, [key]: value };
  fields.forEach((f) => { if (f.when && !f.when(next)) next[f.key] = ""; });
  return next;
};

const missingFields = (spec, v) => Object.fromEntries(
  visibleFields(spec.fields, v).filter((f) => f.required && !String(v?.[f.key] ?? "").trim())
    .map((f) => [f.key, `${f.label} is required`]));

function Field({ field, value, onChange, ctx, idPrefix, errors }) {
  const id = `${idPrefix}-${field.key}`;
  const label = field.required ? `${field.label} (required)` : field.label;
  const invalid = invalidClass(errors, field.key);
  // Every box wears its own refusal, in the add row and in the edit dialog alike.
  const wrap = (control) => (
    <div data-field={field.key} aria-invalid={errors?.[field.key] ? true : undefined}>
      {control}
      <FieldError field={field.key} errors={errors} />
    </div>
  );
  if (field.type === "combo") {
    return wrap(
      <ComboBox id={id} testid={id} value={value || ""} onChange={onChange}
                options={field.options(ctx)} className={invalid}
                placeholder={field.required ? `${field.label} *` : field.label} />
    );
  }
  if (field.type === "select") {
    return wrap(
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger id={id} aria-label={label} data-testid={id} className={invalid}>
          <SelectValue placeholder={field.required ? `${field.label} *` : field.label} />
        </SelectTrigger>
        <SelectContent>
          {field.options(ctx).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  // The add row has no visible labels, only placeholders, so the mark has to live in
  // the placeholder text — it cannot be coloured there.
  return wrap(
    <Input id={id} data-testid={id} placeholder={field.required ? `${field.label} *` : field.label}
           aria-label={label} value={value || ""} onChange={(e) => onChange(e.target.value)} className={invalid} />
  );
}

export default function Masters() {
  const { user } = useAuth();
  const writable = canWrite(user);
  const [rows, setRows] = useState({ units: [], departments: [], trainers: [] });
  const [employees, setEmployees] = useState([]);
  const [hints, setHints] = useState({ organizations: [], expertise_areas: [] });
  const [draft, setDraft] = useState(() =>
    Object.fromEntries(KINDS.map((k) => [k, blankOf(MASTERS[k])])));
  const [editing, setEditing] = useState(null);   // { kind, row, form }
  const [retired, setRetired] = useState(null);   // { kind: rows } once asked for
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState({ units: "", departments: "", trainers: "" });
  const [peek, setPeek] = useState(null);
  const addErr = useFieldErrors();
  const editErr = useFieldErrors();

  const load = async () => {
    try {
      const [u, d, t, e, s] = await Promise.all([
        api.get("/units"), api.get("/departments"), api.get("/trainers"),
        api.get("/employees"), api.get("/suggestions")]);
      setRows({ units: u.data, departments: d.data, trainers: t.data });
      setEmployees(e.data);
      setHints(s.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not load master data"));
    }
  };
  useEffect(() => { load(); }, []);

  const ctx = { units: rows.units, employees, hints };

  // Three fixed calls rather than one inside the tab loop: hooks cannot run in a loop.
  const shown = {
    units: useSearch(rows.units, query.units, SEARCH_FIELDS.units),
    departments: useSearch(rows.departments, query.departments, SEARCH_FIELDS.departments),
    trainers: useSearch(rows.trainers, query.trainers, SEARCH_FIELDS.trainers),
  };

  const add = async (kind) => {
    const spec = MASTERS[kind];
    const gaps = missingFields(spec, draft[kind]);
    if (Object.keys(gaps).length) return addErr.report({ fields: gaps });
    setBusy(true);
    try {
      await api.post(`/${kind}`, draft[kind]);
      setDraft((c) => ({ ...c, [kind]: blankOf(spec) }));
      addErr.clear();
      toast.success(`${cap(spec.noun)} added`);
      load();
    } catch (e) { addErr.report(e, { known: spec.fields.map((f) => f.key) }); }
    setBusy(false);
  };

  const saveEdit = async () => {
    const { kind, row, form } = editing;
    const spec = MASTERS[kind];
    const gaps = missingFields(spec, form);
    if (Object.keys(gaps).length) return editErr.report({ fields: gaps });
    setBusy(true);
    try {
      // PUT rewrites every column the table owns, and the columns a form does not show
      // fall back to the model's defaults. Sending only the visible fields would blank
      // an internal trainer's employee_id, so the whole row goes back with the edits on
      // top of it. Extra keys (id, created_at) are ignored by the model.
      await api.put(`/${kind}/${row.id}`, { ...row, ...form });
      toast.success(`${cap(spec.noun)} updated`);
      setEditing(null);
      editErr.clear();
      load();
    } catch (e) {
      // Left open on failure so the corrections typed are not thrown away.
      editErr.report(e, { known: spec.fields.map((f) => f.key) });
    }
    setBusy(false);
  };

  // A soft delete with a restore route on the other side, and no side effects — so it
  // is simply offered back rather than guarded by a second question. (Retiring an
  // EMPLOYEE keeps its dialog, on the Employees screen: that one also revokes their
  // login, and restoring does not give the login back, so it is not something an Undo
  // can honestly reverse.)
  //
  // This used to sit behind an AlertDialog anyway, so the screen asked you to confirm
  // and THEN offered to undo — and the dialog's own words were "It cannot be undone from
  // here", which the very next toast disproved. A dialog that is wrong about itself is
  // how every dialog stops being read, including the two that matter. The refusal when
  // something still points at the row is a message with a count in it, which is where
  // that warning belonged all along.
  const removeRow = async (kind, row) => {
    setBusy(true);
    await performWithUndo({
      action: () => api.delete(`/${kind}/${row.id}`),
      undo: () => api.post(`/${kind}/${row.id}/restore`),
      message: `${row.name} deleted`,
      undoneMessage: `${row.name} is back`,
      onSettled: () => { load(); if (retired?.[kind]) loadRetired(kind); },
    });
    setBusy(false);
  };

  // Deleting is a soft delete and every other read filters it out, so without this the
  // only way back was psql.
  const loadRetired = async (kind) => {
    try {
      const r = await api.get(`/${kind}`, { params: { deleted: true } });
      setRetired({ kind, rows: r.data });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Could not load deleted records")); }
  };

  const restore = async (kind, row) => {
    try {
      await api.post(`/${kind}/${row.id}/restore`);
      toast.success(`${row.name} restored`);
      loadRetired(kind);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Could not restore")); }
  };

  const editForm = (kind, row) => {
    // Seeded from the row so a field left untouched keeps exactly what it had.
    const spec = MASTERS[kind];
    setEditing({ kind, row, form: Object.fromEntries(spec.fields.map((f) => [f.key, row[f.key] ?? ""])) });
  };

  return (
    <div className="space-y-5" data-testid="masters-page">
      <div>
        <div className="overline text-primary">Master data</div>
        <h2 className="font-heading font-bold text-3xl mt-1">Units, Departments &amp; Trainers</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Reference data for every training. Renaming keeps every link intact — nothing is orphaned —
          but it also changes how past records read, including certificates already issued.
        </p>
      </div>

      <Tabs defaultValue="units">
        <TabsList>
          {KINDS.map((k) => (
            <TabsTrigger key={k} value={k} data-testid={`tab-${k}`}>{MASTERS[k].title}</TabsTrigger>
          ))}
        </TabsList>

        {KINDS.map((kind) => {
          const spec = MASTERS[kind];
          const items = shown[kind];
          return (
            <TabsContent key={kind} value={kind} className="mt-4">
              <div className="space-y-3">
                {writable && (
                  <Card className="p-4 border border-border grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {visibleFields(spec.fields, draft[kind]).map((f) => (
                      <Field key={f.key} field={f} ctx={ctx} idPrefix={`${kind}-new`} errors={addErr.errors}
                             value={draft[kind][f.key]}
                             onChange={(v) => { addErr.clear(f.key); setDraft((c) => ({ ...c, [kind]: applyFieldChange(spec.fields, c[kind], f.key, v) })); }} />
                    ))}
                    <Button onClick={() => add(kind)} disabled={busy} data-testid={`add-${kind}`} className="self-start">
                      <Plus className="w-4 h-4 mr-1" /> Add {spec.noun}
                    </Button>
                  </Card>
                )}

                <SearchInput value={query[kind]} onChange={(v) => setQuery((c) => ({ ...c, [kind]: v }))}
                             placeholder={`Search ${spec.title.toLowerCase()}...`} testid={`search-${kind}`} />

                <Card className="border border-border overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm table-zebra">
                      <thead className="bg-muted/70 border-b border-border">
                        <tr className="text-left">
                          {spec.columns.map((c) => <th key={c.key} className="px-4 py-3">{c.label}</th>)}
                          <th className="px-4 py-3 text-right">{writable && "Actions"}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((it) => (
                          <tr key={it.id} className="border-b border-border last:border-0">
                            {spec.columns.map((c) => (
                              <td key={c.key} className="px-4 py-2">
                                {/* A trainer's name opens what they have actually done. The row
                                    carried a type and an organisation and nothing that happened. */}
                                {kind === "trainers" && c.key === "name" ? (
                                  <button type="button" onClick={() => setPeek(it.id)}
                                          className="text-left font-medium hover:underline"
                                          data-testid={`open-trainer-${it.id}`}>
                                    {it[c.key]}
                                  </button>
                                ) : c.render ? c.render(it, ctx) : it[c.key]}
                              </td>
                            ))}
                            <td className="px-4 py-2">
                              {writable && (
                                <div className="flex justify-end gap-1">
                                  <Button variant="ghost" size="icon" onClick={() => editForm(kind, it)}
                                          aria-label={`Edit ${it.name}`} data-testid={`edit-${kind}-${it.id}`}>
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  <Button variant="ghost" size="icon" onClick={() => removeRow(kind, it)}
                                          aria-label={`Delete ${it.name}`} data-testid={`delete-${kind}-${it.id}`}>
                                    <Trash2 className="w-4 h-4 text-destructive-text" />
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                        {items.length === 0 && (
                          <tr>
                            <td colSpan={spec.columns.length + 1} className="px-4 py-8 text-center text-muted-foreground">
                              {query[kind] ? `No ${spec.title.toLowerCase()} match "${query[kind]}"` : `No ${spec.title.toLowerCase()} yet`}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </Card>

                {writable && (
                  <Card className="p-4 border border-border">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <h3 className="font-heading font-semibold text-sm">Deleted {spec.title.toLowerCase()}</h3>
                      <Button variant="outline" size="sm" data-testid={`toggle-deleted-${kind}`}
                              onClick={() => (retired?.kind === kind ? setRetired(null) : loadRetired(kind))}>
                        {retired?.kind === kind ? "Hide" : "Show deleted"}
                      </Button>
                    </div>
                    {retired?.kind === kind && (
                      retired.rows.length === 0
                        ? <p className="text-sm text-muted-foreground mt-3">Nothing deleted.</p>
                        : <div className="mt-3 divide-y divide-border">
                            {retired.rows.map((r) => (
                              <div key={r.id} className="flex items-center justify-between py-2 gap-3">
                                <span className="text-sm">{r.name}</span>
                                <Button variant="outline" size="sm" onClick={() => restore(kind, r)} data-testid={`restore-${kind}-${r.id}`}>
                                  <Undo2 className="w-3.5 h-3.5 mr-1.5" /> Restore
                                </Button>
                              </div>
                            ))}
                          </div>
                    )}
                  </Card>
                )}
              </div>
            </TabsContent>
          );
        })}
      </Tabs>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) { setEditing(null); editErr.clear(); } }}>
        <DialogContent data-testid="edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit {editing ? MASTERS[editing.kind].noun : ""}</DialogTitle>
            <DialogDescription>
              {editing?.row?.name
                ? `Correcting “${editing.row.name}”. Everything already pointing at this record follows the change — including trainings that already happened and certificates already issued, which will read the new name when somebody verifies them. Correct a mistake here; do not repurpose the record for something else.`
                : ""}
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="grid gap-3 py-2">
              {visibleFields(MASTERS[editing.kind].fields, editing.form).map((f) => (
                <div key={f.key} className="grid gap-1.5">
                  <FieldLabel htmlFor={`${editing.kind}-edit-${f.key}`} required={f.required}>{f.label}</FieldLabel>
                  <Field field={f} ctx={ctx} idPrefix={`${editing.kind}-edit`} errors={editErr.errors}
                         value={editing.form[f.key]}
                         onChange={(v) => { editErr.clear(f.key); setEditing((c) => ({ ...c, form: applyFieldChange(MASTERS[c.kind].fields, c.form, f.key, v) })); }} />
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={busy} data-testid="edit-save">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TrainerPeek id={peek} onClose={() => setPeek(null)} />

    </div>
  );
}
